// familyTreeLineage.ts — pure, deterministic ancestry colours and tracing.
// No schema changes, remote assets, or additional runtime dependencies.
import type { Layout, TreeLink, TreePerson } from "./tree";

export const FAMILY_PALETTE = [
  { key: "plum", name: "Plum", token: "tree-line-1", dash: undefined },
  { key: "amber", name: "Amber", token: "tree-line-2", dash: "7 3" },
  { key: "blue", name: "Blue", token: "tree-line-3", dash: "2 3" },
  { key: "green", name: "Green", token: "tree-line-4", dash: "9 2 2 2" },
  { key: "coral", name: "Coral", token: "tree-line-5", dash: "5 2" },
  { key: "violet", name: "Violet", token: "tree-line-6", dash: "1 3" },
  { key: "teal", name: "Teal", token: "tree-line-7", dash: "10 3" },
  { key: "olive", name: "Olive", token: "tree-line-8", dash: "3 2 1 2" },
] as const;

export type AncestryPart = { originId: string; paletteIndex: number; fraction: number };
export type Ancestry = { parts: AncestryPart[]; dominant: number };
export type Bloodlines = {
  byPerson: Map<string, Ancestry>;
  // Roots are people without recorded parents, in their visible left-to-right order.
  roots: { person: TreePerson; paletteIndex: number }[];
};

export function calculateBloodlines(people: TreePerson[], links: TreeLink[], layout: Layout): Bloodlines {
  const byId = new Map(people.map((person) => [person.id, person]));
  const parents = new Map(people.map((p) => [p.id, [] as string[]]));
  for (const link of links) {
    if (link.kind !== "parent" || !byId.has(link.from_id) || !byId.has(link.to_id)) continue;
    const arr = parents.get(link.to_id)!;
    if (!arr.includes(link.from_id)) arr.push(link.from_id);
  }
  const roots = people.filter((p) => !parents.get(p.id)?.length).sort((a, b) =>
    (layout.nodes.get(a.id)?.gen ?? Number.MAX_VALUE) - (layout.nodes.get(b.id)?.gen ?? Number.MAX_VALUE) ||
    (layout.nodes.get(a.id)?.x ?? Number.MAX_VALUE) - (layout.nodes.get(b.id)?.x ?? Number.MAX_VALUE) ||
    a.id.localeCompare(b.id),
  );
  // Earliest recorded founder generation receives four distinct colours first.
  // Reordering/adding founder branches can reassign colours; pinning founder colours
  // persistently would need a future OPTIONAL preference, not a genealogy DB rewrite.
  // A chosen colour wins (038); the rest are handed out left to right, skipping colours already taken.
  const rootColours = new Map<string, number>();
  const taken = new Set<number>();
  for (const p of roots) if (p.branch_colour && p.branch_colour >= 1 && p.branch_colour <= FAMILY_PALETTE.length) { rootColours.set(p.id, p.branch_colour - 1); taken.add(p.branch_colour - 1); }
  let next = 0;
  for (const p of roots) {
    if (rootColours.has(p.id)) continue;
    let tries = 0;
    while (taken.has(next % FAMILY_PALETTE.length) && tries++ < FAMILY_PALETTE.length) next++;
    rootColours.set(p.id, next % FAMILY_PALETTE.length); taken.add(next % FAMILY_PALETTE.length); next++;
  }
  const cache = new Map<string, Ancestry>();
  const visiting = new Set<string>();
  const visit = (id: string): Ancestry => {
    const old = cache.get(id);
    if (old) return old;
    // Fail safely on corrupt/cyclic imported relationships rather than recursing forever.
    if (visiting.has(id)) return { parts: [], dominant: 0 };
    visiting.add(id);
    const ps = parents.get(id) ?? [];
    const contributions = new Map<string, number>();
    if (ps.length) {
      for (const parentId of ps) {
        for (const part of visit(parentId).parts) {
          contributions.set(part.originId, (contributions.get(part.originId) ?? 0) + part.fraction / ps.length);
        }
      }
    }
    if (!contributions.size) contributions.set(id, 1);
    const total = [...contributions.values()].reduce((sum, part) => sum + part, 0);
    const parts: AncestryPart[] = [...contributions].map(([originId, value]) => ({
      originId,
      paletteIndex: rootColours.get(originId) ?? rootColours.get(id) ?? 0,
      fraction: value / total,
    })).sort((a, b) => a.paletteIndex - b.paletteIndex || a.originId.localeCompare(b.originId));
    const dominant = [...parts].sort((a, b) => b.fraction - a.fraction)[0]?.paletteIndex ?? 0;
    const result = { parts, dominant };
    visiting.delete(id);
    cache.set(id, result);
    return result;
  };
  for (const p of people) visit(p.id);
  return { byPerson: cache, roots: roots.map((person) => ({ person, paletteIndex: rootColours.get(person.id) ?? 0 })) };
}

export type TreeTrace = {
  ids: Set<string>;
  parentLinkIds: Set<string>;
  partnerLinkIds: Set<string>;
  ancestorIds: Set<string>;
  descendantIds: Set<string>;
};

export function traceFamily(links: TreeLink[], selectedId: string | null): TreeTrace {
  const trace: TreeTrace = {
    ids: new Set(), parentLinkIds: new Set(), partnerLinkIds: new Set(),
    ancestorIds: new Set(), descendantIds: new Set(),
  };
  if (!selectedId) return trace;
  trace.ids.add(selectedId);
  const above = new Map<string, TreeLink[]>();
  const below = new Map<string, TreeLink[]>();
  for (const link of links) {
    if (link.kind === "partner") {
      if (link.from_id === selectedId || link.to_id === selectedId) {
        trace.ids.add(link.from_id === selectedId ? link.to_id : link.from_id);
        trace.partnerLinkIds.add(link.id);
      }
    } else {
      if (!above.has(link.to_id)) above.set(link.to_id, []);
      if (!below.has(link.from_id)) below.set(link.from_id, []);
      above.get(link.to_id)!.push(link);
      below.get(link.from_id)!.push(link);
    }
  }
  const walk = (adjacent: Map<string, TreeLink[]>, ancestor: boolean) => {
    const stack = [selectedId];
    const visited = new Set(stack);
    while (stack.length) {
      const current = stack.pop()!;
      for (const edge of adjacent.get(current) ?? []) {
        trace.parentLinkIds.add(edge.id);
        const other = ancestor ? edge.from_id : edge.to_id;
        trace.ids.add(other);
        (ancestor ? trace.ancestorIds : trace.descendantIds).add(other);
        if (!visited.has(other)) { visited.add(other); stack.push(other); }
      }
    }
  };
  walk(above, true);
  walk(below, false);
  return trace;
}

/**
 * Compress accidental multi-slot whitespace produced by the iterative layout.
 * Only visual x positions change; family groups and link relationships stay
 * exactly as layoutTree returned. Keeps 11-person families readable on phones.
 */
export function compactLayout(layout: Layout, maxGap = 1.55): Layout {
  const xValues = [...new Set([...layout.nodes.values()].map((node) => node.x))].sort((a, b) => a - b);
  if (!xValues.length) return layout;
  const remap = new Map<number, number>();
  let cursor = 0;
  for (let i = 0; i < xValues.length; i++) {
    if (i) cursor += Math.min(maxGap, xValues[i] - xValues[i - 1]);
    remap.set(xValues[i], cursor);
  }
  const nodes = new Map([...layout.nodes].map(([id, node]) => [id, { ...node, x: remap.get(node.x)! }]));
  return { ...layout, nodes, width: Math.max(1, ...[...nodes.values()].map((n) => n.x + 1)) };
}
